import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent, FormatService, ReportTableColumn, SummaryItem } from '../../../../../tms-shared';
import {
  SalesOrderDeliveryReport,
  SalesOrderExtraReport,
  SalesOrderProformaReport,
  SalesOrderProformaReportLine,
  SalesOrderReportLineProcessing,
} from '../../../../models';
import { SalesOrderReportsService, SalesOrderService } from '../../../../services';

@Component({
  selector: 'app-proforma-report',
  templateUrl: './proforma-report.component.html',
  styleUrl: './proforma-report.component.scss',
})
export class ProformaReportComponent extends BaseComponent implements OnInit {
  @Input() salesOrderHeaderId: string;

  model$: Observable<SalesOrderProformaReport>;

  constructor(
    private salesOrderReportsService: SalesOrderReportsService,
    private salesOrderService: SalesOrderService,
    private formatService: FormatService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit() {
    if (!this.salesOrderHeaderId) {
      this.salesOrderHeaderId = this.route.snapshot.paramMap.get('id');
    }
    this.getModel();

    this.salesOrderService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.getModel());
  }

  private getModel() {
    this.model$ = this.salesOrderReportsService.getSalesOrderProforma(this.salesOrderHeaderId);
  }

  headerSummaryItems(model: SalesOrderProformaReport): SummaryItem[] {
    return [
      { label: $localize`Proforma Number`, value: model.orderNo },
      { label: $localize`Your Order Number`, value: model.customerOrderNo },
      { label: $localize`Your Order Reference`, value: model.customerOrderRef },
      { label: $localize`Customer Account`, value: model.customerKey },
      { label: $localize`Proforma Date`, value: this.formatService.date(model.dateOrdered) },
      { label: $localize`Ordered By`, value: model.orderedBy },
    ];
  }

  confirmationSummaryItems(model: SalesOrderProformaReport): SummaryItem[] {
    return model.customerConfirmationEmailAddress
      ? [{ label: $localize`Proforma emailed to`, value: model.customerConfirmationEmailAddress }]
      : [];
  }

  lineColumns(model: SalesOrderProformaReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: this.formatService.valuesToHtml(this.formatProductDescription(model)),
      },
      {
        label: $localize`Quantity`,
        value: `${this.formatService.number(model.quantity)} ${model.unit}`,
        textRight: true,
      },
      {
        label: $localize`Price`,
        value: `${model.currencySymbol}${this.formatService.number(model.price)} ${model.priceListUnit}`,
        textRight: true,
      },
      {
        label: $localize`Disc %`,
        value: this.formatService.number(model.discount),
        textRight: true,
      },
      {
        label: $localize`Disc Price`,
        value: this.formatService.number(model.discountedPrice),
        textRight: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
      },
    ];
  }

  lineSubProcessingTableColumns(model: SalesOrderReportLineProcessing): ReportTableColumn[] {
    return [
      { label: $localize`Process`, value: model.processProductKey },
      { label: $localize`Description`, value: model.processProductDescription },
      { label: $localize`Price`, value: `${model.currencySymbol}${model.price}` },
      { label: $localize`Value`, value: `${model.currencySymbol}${model.value}` },
    ];
  }

  deliveryColumns(model: SalesOrderDeliveryReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Price`,
        value: this.formatService.currency(model.price, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  extraColumns(model: SalesOrderExtraReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Price`,
        value: this.formatService.currency(model.price, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  footerSummaryItems(model: SalesOrderProformaReport): SummaryItem[][] {
    return [
      [
        { label: $localize`Currency`, value: model.currency },
        { label: model.agentName ? $localize`Agent` : '', value: model.agentName },
        { label: model.paymentTerms ? $localize`Payment Terms` : '', value: model.paymentTerms },
        { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
        { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
        { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
        { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
        { label: model.shippingNotes ? $localize`Shipping Notes` : '', value: model.shippingNotes },
        { label: model.notes ? $localize`Notes` : '', value: model.documentNotes },
        {
          label: model.deliveryInstructions ? $localize`Delivery Instructions` : '',
          value: model.deliveryInstructions,
        },
        { label: $localize`Account Name`, value: model.bankAccountName },
        { label: $localize`Bank`, value: model.bankName },
        { label: model.bankIBAN ? $localize`IBAN` : '', value: model.bankIBAN },
        { label: model.bankSwift ? $localize`BIC/Swift` : '', value: model.bankSwift },
        { label: $localize`Sort Code`, value: `${model.bankSortCode ?? ''}` },
        { label: $localize`Account`, value: `${model.bankAccount ?? ''}` },
      ],
      [
        {
          label: $localize`Total Goods`,
          value: this.formatService.currency(model.goodsValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: model.processValue > 0 ? $localize`Process Cost` : '',
          value: model.processValue > 0 ? this.formatService.currency(model.processValue, model.currencySymbol) : '',
          textRight: true,
        },
        {
          label: $localize`Total Delivery`,
          value: this.formatService.currency(model.totalDeliveryValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`Total Extras`,
          value: this.formatService.currency(model.totalExtraValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`Nett Total`,
          value: this.formatService.currency(model.nettValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`VAT` + ' @ ' + (model.taxRate > 0 ? ' ' + model.taxRate + '%' : ''),
          value: this.formatService.currency(model.taxValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`Overall Total`,
          value: this.formatService.currency(model.overallValue, model.currencySymbol),
          textRight: true,
        },
      ],
    ];
  }

  proformaPaymentNarrative(model: SalesOrderProformaReport): SummaryItem[] {
    return model.bankNarrative ? [{ label: $localize`Payment Note`, value: model.bankNarrative }] : [];
  }

  valuesSummaryItems(model: SalesOrderProformaReport): SummaryItem[] {
    return;
  }

  private formatProductDescription(model: SalesOrderProformaReportLine): string[] {
    const values = [model.productKey, model.productDescription];
    if (model.customerProduct) {
      values.push($localize`Customer Product` + ' ' + model.customerProduct);
    }
    if (model.customerLineRef) {
      values.push($localize`Order Reference` + ' ' + model.customerLineRef);
    }
    if (model.origin) {
      values.push($localize`Country of Origin` + ' ' + model.origin);
    }
    if (model.commodityCode) {
      values.push($localize`Commodity Code` + ' ' + model.commodityCode);
    }
    if (model.makeUp) {
      values.push($localize`Make Up` + ' ' + model.makeUp);
    }
    return values;
  }
}
